// Her SITES — the Level 1 of the Sites tool.
//
// Her call, 2026-09-02: "start building multiple sites now; landing pages are
// the same as websites so it can stay Sites and we can add Level 1 right away;
// we'll just change the recommended pages in a landing page." So a site is a
// real record with a KIND, and the kind is the whole difference between a main
// site and a landing site: it decides which standard pages the Add page menu
// offers. It is not a different table and not a different screen.
//
// Backed by `sites` from supabase/0015_sites_and_courses.sql, whose backfill
// gave every account that had pages one site called "My site" — so every
// account that was already using the tool arrived with one. An account made
// AFTER that migration ran did not, and gets its first site here, on its own,
// the way the Website studio has always made the first Homepage.
//
// AN ADDRESS IS UNIQUE WITHIN A SITE, which needs supabase/0016 as well as 0015
// — 0001's per-account rule would reject the second site's /home. Adding a site
// before 0016 has run fails on the first standard page, with a Postgres
// duplicate-key error rather than anything she could act on.

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { createRecordStore, derivedId } from "./recordStore";
import { reorderWrites } from "./reorder";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
export const SITE_FIELDS = ["name", "kind", "domain", "offerId", "sortOrder", "status"];

export const MAIN_SITE = "main";
export const LANDING_SITE = "landing";

/* What the two kinds are, in her words rather than the column's. The `pages`
   list is which standard pages that kind's Add page menu offers — see
   PAGE_KINDS in websitePages.js, which is where they are actually defined.

   The landing list is a PROPOSAL, and the spec says so (decision 8: "a landing
   site's list is the open design item: probably Landing page and Thank-you
   page, with Other as always"). It is one array to change when she has a view. */
export const SITE_KINDS = [
  {
    id: MAIN_SITE,
    label: "Main site",
    hint: "Your whole website — home, services, about, contact",
  },
  {
    id: LANDING_SITE,
    label: "Landing site",
    hint: "One offer, one page, one action — plus the thank-you it lands on",
  },
];

export const siteKind = (site) =>
  SITE_KINDS.some((k) => k.id === site?.kind) ? site.kind : MAIN_SITE;

export const siteKindDef = (site) =>
  SITE_KINDS.find((k) => k.id === siteKind(site)) || SITE_KINDS[0];

/** What a site is called in a list. Never blank — an unnamed row in a switcher
 *  is a row she cannot tell from the one above it. */
export const siteLabel = (site) => String(site?.name || "").trim() || "Untitled site";

/* Ordered by `sort_order` ascending, like pages and modules: this is a list she
   arranges, not a feed she adds to. */
const useSiteRecords = createRecordStore({
  table: "sites",
  fields: SITE_FIELDS,
  idPrefix: "ste",
  orderBy: "sort_order",
  ascending: true,
});

export function useSites() {
  const { rows, loading, error, save, remove, reload, clearError, newId } = useSiteRecords();

  /* Sorted here as well as in the query, for the same reason pages are: `save`
     replaces a row in place, so a reorder writes to Supabase and leaves the
     array exactly as it was until a reload. */
  const sites = useMemo(
    () => [...rows].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0)),
    [rows],
  );

  const addSite = useCallback(async (kind = MAIN_SITE) => {
    const def = SITE_KINDS.find((k) => k.id === kind) || SITE_KINDS[0];
    return save({
      id: newId(),
      /* Named for what it is, not "Untitled". She renames it behind the cog the
         moment she knows what it is for, and a list reading "Main site /
         Landing site" is still readable if she never does. */
      name: def.id === MAIN_SITE ? "New site" : "New landing site",
      kind: def.id,
      status: "draft",
      sortOrder: sites.reduce((max, s) => Math.max(max, (s?.sortOrder ?? 0) + 1), 0),
    });
  }, [sites, save, newId]);

  /* HER FIRST SITE, made on its own.
     0015's backfill gave a site to every account that had pages — which is
     every account that was using the tool, and NO account made after it ran. So
     without this a new client opens Sites, has no site, and every Add page in
     the menu does nothing: `addStandardPage` has nowhere to put a page. The
     automatic Homepage would never fire either. A brand-new account would find
     a tool that cannot be used at all, silently.

     Four guards, each load-bearing and each the same one the automatic Homepage
     uses (websitePages.js):
       - `loading` — creating against a list that hasn't arrived makes a second
         site for an account that already has one.
       - `error` — a failed load leaves `rows` empty, which is NOT an empty
         table. Without this, one dropped connection makes a spare site.
       - `sites.length` + `madeOnce` — only when there are none, and only once
         per session.
     And the id is DERIVED from the account, so two browsers opening at the same
     moment upsert one row rather than inserting two. */
  const { user } = useUser();
  const making = useRef(false);
  const madeOnce = useRef(false);
  useEffect(() => {
    if (loading || error || making.current || madeOnce.current || sites.length || !user?.id) return;
    making.current = true;
    madeOnce.current = true;
    save({
      id: derivedId("ste", user.id),
      /* The same name the backfill gave everyone else's, so two accounts do not
         start with two different words for the same thing. */
      name: "My site",
      kind: MAIN_SITE,
      status: "draft",
      sortOrder: 0,
    }).finally(() => { making.current = false; });
  }, [loading, error, sites.length, user?.id, save]);

  const reorderSites = useCallback(async (from, to) => {
    let ok = true;
    for (const s of reorderWrites(sites, from, to)) if (!(await save(s))) ok = false;
    return ok;
  }, [sites, save]);

  return {
    sites, loading, error, clearError, reload,
    saveSite: save,
    /* DELETING A SITE DELETES ITS PAGES — `website_pages.site_id` is
       `on delete cascade`, because a page with no site is a page no screen can
       reach. Postgres does the deleting; the caller's job is to say so first,
       with the page count, in a ConfirmDialog. */
    deleteSite: remove,
    addSite,
    reorderSites,
    newSiteId: newId,
  };
}
